import { useEffect, useState } from 'react';

import { Link } from 'react-router';

import { CheckCircle2, XCircle } from 'lucide-react';

import { useVerifyEmailMutation } from '@/features/auth/store/api';
import { PrimaryButton, GlassCard } from '@/lib/ui';
import { ROUTES } from '@/routes';

import { useUrlToken } from '../hooks';

type VerifyStatus = 'loading' | 'success' | 'error';

export function VerifyEmailView() {
  const token = useUrlToken();

  const [verifyEmail] = useVerifyEmailMutation();
  const [status, setStatus] = useState<VerifyStatus>('loading');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }
    verifyEmail({ token })
      .unwrap()
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'));
    // verifyEmail is stable — intentionally omitted from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="w-full max-w-md space-y-6">
      <div className="flex justify-center">
        <img src="/moveris-logo-dark.svg" alt="Moveris" className="h-10 w-auto" />
      </div>

      <GlassCard padding="lg" className="!bg-white/[0.07]">
        {status === 'loading' && (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            {/* Simple CSS spinner — no extra dep */}
            <div
              className="size-10 animate-spin rounded-full border-4 border-white/20 border-t-white"
              role="status"
              aria-label="Verifying your email"
            />
            <p className="text-sm text-white/60">Verifying your email address…</p>
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <CheckCircle2 className="size-12 text-success" aria-hidden="true" />
            <div className="space-y-1">
              <h1 className="text-xl font-semibold text-white">Email verified!</h1>
              <p className="text-sm text-white/60">
                If your account still needs approval, our team will reach out before you can sign
                in. New organizations are set up after your first sign-in.
              </p>
            </div>
            <PrimaryButton variant="success" asChild>
              <Link to={ROUTES.login}>Go to sign in</Link>
            </PrimaryButton>
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col items-center gap-4 py-4 text-center">
            <XCircle className="size-12 text-destructive" aria-hidden="true" />
            <div className="space-y-1">
              <h1 className="text-xl font-semibold text-white">Verification failed</h1>
              <p className="text-sm text-white/60">
                The link may have expired or already been used. Request a new one from the sign-in
                page.
              </p>
            </div>
            <Link to={ROUTES.login} className="text-sm font-medium text-white hover:underline">
              Back to sign in
            </Link>
          </div>
        )}
      </GlassCard>
    </div>
  );
}
